/**
 * Poll Display Component
 * Displays poll results and allows voting/changing votes
 */

import React, { useState, useEffect } from 'react';
import { useVoteOnPoll, useChangeVote, useClosePoll } from '../../hooks/usePolls';
import { usePollRealtime } from '../../hooks/usePollRealtime';
import { useAuthStore } from '../../store/auth.store';
import type { Poll } from '../../types/api.types';

interface PollDisplayProps {
  poll: Poll;
}

export const PollDisplay: React.FC<PollDisplayProps> = ({ poll }) => {
  const { user } = useAuthStore();
  const [selectedOptions, setSelectedOptions] = useState<string[]>(poll.userVotes || []);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  const voteOnPollMutation = useVoteOnPoll();
  const changeVoteMutation = useChangeVote();
  const closePollMutation = useClosePoll();

  // Enable real-time updates for this poll
  usePollRealtime(poll.id);

  const hasVoted = poll.userVotes && poll.userVotes.length > 0;
  const canVote = !poll.isExpired && poll.userVotes.length === 0;
  const canChangeVote = !poll.isExpired && poll.userVotes.length > 0;

  // Clear the countdown when the poll has no active expiry (adjust state during render)
  if ((!poll.expires_at || poll.isExpired) && timeRemaining !== '') {
    setTimeRemaining('');
  }

  // Calculate time remaining
  useEffect(() => {
    if (!poll.expires_at || poll.isExpired) {
      return;
    }

    const calculateTimeRemaining = () => {
      const now = new Date();
      const expiresAt = new Date(poll.expires_at!);
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Изтекла');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeRemaining(`${days}д ${hours}ч`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}ч ${minutes}м`);
      } else {
        setTimeRemaining(`${minutes}м`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [poll.expires_at, poll.isExpired]);

  // Handle option selection
  const handleOptionToggle = (optionId: string) => {
    if (poll.isExpired) return;

    if (poll.is_multiple_choice) {
      // Multiple choice: toggle selection
      setSelectedOptions((prev) =>
        prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId]
      );
    } else {
      // Single choice: replace selection
      setSelectedOptions([optionId]);
    }
  };

  // Handle vote submission
  const handleVote = async () => {
    if (selectedOptions.length === 0) return;

    try {
      if (hasVoted) {
        await changeVoteMutation.mutateAsync({
          pollId: poll.id,
          optionIds: selectedOptions,
        });
      } else {
        await voteOnPollMutation.mutateAsync({
          pollId: poll.id,
          optionIds: selectedOptions,
        });
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  // Handle close poll
  const handleClosePoll = async () => {
    if (!window.confirm('Сигурни ли сте, че искате да затворите анкетата?')) {
      return;
    }

    try {
      await closePollMutation.mutateAsync(poll.id);
    } catch (error) {
      console.error('Failed to close poll:', error);
    }
  };

  // Check if user can close poll (creator or admin)
  const canClosePoll = user && (user.role === 'admin' || user.role === 'super_admin' || poll.message_id.includes(user.id));

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        <span className="text-xl">📊</span>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{poll.question}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-600 dark:text-gray-400">
            <span>
              {poll.is_multiple_choice ? 'Множествен избор' : 'Единичен избор'}
            </span>
            <span>•</span>
            <span>{poll.is_anonymous ? 'Анонимна' : 'Публична'}</span>
            {timeRemaining && (
              <>
                <span>•</span>
                <span className={poll.isExpired ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}>
                  {poll.isExpired ? 'Изтекла' : `Изтича след ${timeRemaining}`}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-2 mb-4">
        {poll.options
          .sort((a, b) => a.option_order - b.option_order)
          .map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            const hasUserVoted = poll.userVotes.includes(option.id);

            return (
              <div key={option.id}>
                {/* Option Button */}
                <button
                  onClick={() => handleOptionToggle(option.id)}
                  disabled={poll.isExpired}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    poll.isExpired
                      ? 'cursor-not-allowed opacity-75'
                      : 'hover:border-blue-400 hover:shadow-sm'
                  } ${
                    isSelected || hasUserVoted
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                      : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {/* Checkbox/Radio */}
                    <div
                      className={`w-5 h-5 rounded ${
                        poll.is_multiple_choice ? 'rounded-md' : 'rounded-full'
                      } border-2 flex items-center justify-center ${
                        isSelected || hasUserVoted
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-400 dark:border-gray-500'
                      }`}
                    >
                      {(isSelected || hasUserVoted) && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Option Text */}
                    <span className="font-medium text-gray-900 dark:text-gray-100">{option.option_text}</span>

                    {/* Percentage */}
                    <span className="ml-auto text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {option.percentage}%
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${option.percentage}%` }}
                    />
                  </div>

                  {/* Vote Count and Voters */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {option.voteCount} {option.voteCount === 1 ? 'глас' : 'гласа'}
                    </span>
                    {!poll.is_anonymous && option.voters && option.voters.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {option.voters.map((v) => v.display_name).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-300 dark:border-gray-700 pt-3 space-y-2">
        {/* Total Votes */}
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Всичко: {poll.totalVotes} {poll.totalVotes === 1 ? 'глас' : 'гласа'}
          </span>
        </div>

        {/* User's Votes */}
        {hasVoted && !poll.is_anonymous && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Вие гласувахте за:{' '}
            {poll.options
              .filter((opt) => poll.userVotes.includes(opt.id))
              .map((opt) => opt.option_text)
              .join(', ')}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Vote/Change Vote Button */}
          {(canVote || canChangeVote) && (
            <button
              onClick={handleVote}
              disabled={
                selectedOptions.length === 0 ||
                voteOnPollMutation.isPending ||
                changeVoteMutation.isPending
              }
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {voteOnPollMutation.isPending || changeVoteMutation.isPending
                ? 'Обработка...'
                : hasVoted
                  ? 'Промени глас'
                  : 'Гласувай'}
            </button>
          )}

          {/* Close Poll Button (Creator/Admin Only) */}
          {canClosePoll && !poll.isExpired && (
            <button
              onClick={handleClosePoll}
              disabled={closePollMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded-md hover:bg-red-100 dark:hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {closePollMutation.isPending ? 'Затваряне...' : 'Затвори анкета'}
            </button>
          )}
        </div>

        {/* Expired/Closed Message */}
        {poll.isExpired && (
          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
            ⚠️ Анкетата е {poll.expires_at ? 'изтекла' : 'затворена'}
          </p>
        )}
      </div>
    </div>
  );
};
